import { memo, CSSProperties } from 'react';
import cn from 'classnames';

import styles from './SelectionCell.module.scss';

export interface SelectionCellProps {
  children: React.ReactNode;
  selected?: boolean;
  disabled?: boolean;
  error?: boolean;
  onClick?: () => void;
  id?: string;
  style?: CSSProperties;
}

// Slash-joined labels ("Short/Standard") have no natural break opportunity
// at the slash in every browser, so a forced wrap (e.g. QuickAdd's
// equal-width grid) can land mid-word instead ("Short/St" / "andard"). A
// <wbr/> right after the slash fixes the break point without changing the
// rendered text or its width when no wrap is needed.
const withSlashBreaks = (node: React.ReactNode): React.ReactNode => {
  if (typeof node !== 'string' || !node.includes('/')) {
    return node;
  }

  const parts = node.split('/');
  return parts.flatMap((part, index) =>
    index === 0
      ? [part]
      : ['/', <wbr key={`wbr-${parts.slice(0, index + 1).join('/')}`} />, part]
  );
};

const SelectionCell = ({
  children,
  selected = false,
  disabled = false,
  error = false,
  onClick,
  id,
  style,
}: SelectionCellProps) => {
  return (
    <button
      type="button"
      id={id}
      className={cn(styles.cell, {
        [styles.cellSelected]: selected,
        [styles.cellDisabled]: disabled,
        [styles.cellError]: error && !disabled,
      })}
      aria-pressed={selected}
      onClick={onClick}
      disabled={disabled}
      style={style}
    >
      {/* A bare text child becomes an anonymous flex item, which gets the
          browser's content-based automatic minimum size and can't be
          targeted by any selector to opt out of it — it refuses to shrink
          or wrap no matter what white-space/overflow-wrap say, overflowing
          a fixed-width cell (e.g. QuickAdd's equal-width grid). Wrapping in
          a real element makes that minimum addressable via .label below. */}
      <span className={styles.label}>{withSlashBreaks(children)}</span>
    </button>
  );
};

export default memo(SelectionCell);
